import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { policiesService } from '../services/policies.service';
import {
  TenantPoliciesResponse,
  TenantPolicy,
  PolicyType,
} from '../components/policies/types';
import {
  DeploymentStatus,
  TrackedDeployment,
  TERMINAL_DEPLOYMENT_STATUSES,
  DEPLOYMENT_TRACKING_STORAGE_KEY,
} from '../services/types';
import type { RootState } from '../store';

/**
 * Read active tracked deployments from localStorage and re-apply
 * "InProgress" status to freshly fetched policies so the UI doesn't
 * flash back to "Not Available" on page refresh.
 */
function rehydrateTenantInProgressStatuses(
  policies: TenantPolicy[],
  targetTenantId: string,
): void {
  try {
    const raw = localStorage.getItem(DEPLOYMENT_TRACKING_STORAGE_KEY);
    if (!raw) return;
    const items: TrackedDeployment[] = JSON.parse(raw);
    // Build a set of active entityId (policyName) keys for this tenant
    const activeEntityIds = new Set<string>();
    items.forEach(item => {
      if (
        !TERMINAL_DEPLOYMENT_STATUSES.has(item.apiStatus) &&
        item.view === 'tenant' &&
        item.tenantId.toLowerCase() === targetTenantId.toLowerCase()
      ) {
        activeEntityIds.add(item.entityId.toLowerCase());
      }
    });
    if (activeEntityIds.size === 0) return;
    // Stamp matching policies as InProgress
    policies.forEach(policy => {
      if (activeEntityIds.has(policy.policyName.toLowerCase())) {
        policy.status = 'InProgress';
      }
    });
  } catch {
    // Silently ignore
  }
}

// ========== State Interface ==========

interface TenantPoliciesState {
  // Data
  policies: TenantPolicy[];
  baseTenantId: string;
  baseTenantName: string;
  targetTenantId: string;
  targetTenantName: string;
  
  // Pagination (client-side)
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;

  // Per-type totals (drive tab badges; independent of policyType filter)
  dlpTotalCount: number;
  autoLabelTotalCount: number;
  
  // Loading & Error states
  loading: boolean;
  error: string | null;
  
  // Metadata
  lastFetched: string | null;
  /** requestId of the most recently dispatched fetch; older responses are discarded. */
  latestRequestId: string | null;
}

const initialState: TenantPoliciesState = {
  policies: [],
  baseTenantId: '',
  baseTenantName: '',
  targetTenantId: '',
  targetTenantName: '',
  pageNumber: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 0,
  hasNextPage: false,
  dlpTotalCount: 0,
  autoLabelTotalCount: 0,
  loading: false,
  error: null,
  lastFetched: null,
  latestRequestId: null,
};

// ========== Async Thunks ==========

/**
 * Fetch tenant-specific policies (DLP + AutoLabel combined)
 * Client-side filtering will be applied in components
 */
export const fetchTenantPolicies = createAsyncThunk<
  TenantPoliciesResponse,
  { tenantId: string; searchTerm?: string; pageNumber?: number; pageSize?: number; policyType?: PolicyType },
  { rejectValue: string }
>(
  'tenantPolicies/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const { tenantId, searchTerm, pageNumber = 1, pageSize = 25, policyType } = params;
      
      const response = await policiesService.fetchTenantPolicies(
        tenantId,
        searchTerm,
        pageNumber,
        pageSize,
        policyType,
      );
      
      // Re-apply InProgress statuses from localStorage before returning
      // (side effect: reads localStorage — must run in thunk, not reducer)
      rehydrateTenantInProgressStatuses(response.policies, response.targetTenantId);
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch tenant policies';
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== Slice ==========

const tenantPoliciesSlice = createSlice({
  name: 'tenantPolicies',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear all data
    clearTenantPolicies: (state) => {
      return initialState;
    },
    
    // Update page number (for client-side pagination)
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = Math.max(1, action.payload);
    },
    
    // Update page size
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = Math.max(1, action.payload);
      state.pageNumber = 1; // Reset to first page when page size changes
    },
    
    /**
     * Update policy deployment statuses after a batch deploy
     * Payload: Map serialized as array of [key, status] where key = "entityId::tenantId"
     */
    updatePolicyDeploymentStatuses: (
      state,
      action: PayloadAction<Array<[string, DeploymentStatus]>>
    ) => {
      const updates = new Map(action.payload);
      state.policies.forEach(policy => {
        // Key matches entityId (policyName) used in deployment API requests
        const key = `${policy.policyName.toLowerCase()}::${state.targetTenantId.toLowerCase()}`;
        const newStatus = updates.get(key);
        if (newStatus) {
          policy.status = newStatus;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tenant policies
      // `latestRequestId` discards superseded responses. Tab / page / search changes and the
      // summary-step refresh can be in flight at the same time; without this guard an older
      // request that resolves last overwrites the fresher deployment status the summary step
      // just fetched, and the summary silently renders stale data.
      .addCase(fetchTenantPolicies.pending, (state, action) => {
        state.latestRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantPolicies.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.policies = action.payload.policies;
        state.baseTenantId = action.payload.baseTenantId;
        state.baseTenantName = action.payload.baseTenantName;
        state.targetTenantId = action.payload.targetTenantId;
        state.targetTenantName = action.payload.targetTenantName;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.hasNextPage = action.payload.hasNextPage;
        if (typeof action.payload.dlpTotalCount === 'number') {
          state.dlpTotalCount = action.payload.dlpTotalCount;
        }
        if (typeof action.payload.autoLabelTotalCount === 'number') {
          state.autoLabelTotalCount = action.payload.autoLabelTotalCount;
        }
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchTenantPolicies.rejected, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch tenant policies';
      });
  },
});

// ========== Actions ==========

export const {
  clearError,
  clearTenantPolicies,
  setPageNumber,
  setPageSize,
  updatePolicyDeploymentStatuses,
} = tenantPoliciesSlice.actions;

// ========== Selectors ==========

export const selectTenantPolicies = (state: RootState) =>
  state.tenantPolicies.policies.filter(p => p.baseRules.length > 0);
export const selectTenantPoliciesLoading = (state: RootState) => state.tenantPolicies.loading;
export const selectTenantPoliciesError = (state: RootState) => state.tenantPolicies.error;
export const selectTenantPoliciesMetadata = (state: RootState) => ({
  baseTenantId: state.tenantPolicies.baseTenantId,
  baseTenantName: state.tenantPolicies.baseTenantName,
  targetTenantId: state.tenantPolicies.targetTenantId,
  targetTenantName: state.tenantPolicies.targetTenantName,
  pageNumber: state.tenantPolicies.pageNumber,
  pageSize: state.tenantPolicies.pageSize,
  totalCount: state.tenantPolicies.totalCount,
  totalPages: state.tenantPolicies.totalPages,
  hasNextPage: state.tenantPolicies.hasNextPage,
  dlpTotalCount: state.tenantPolicies.dlpTotalCount,
  autoLabelTotalCount: state.tenantPolicies.autoLabelTotalCount,
  lastFetched: state.tenantPolicies.lastFetched,
});

// Selector to get DLP policies only
export const selectTenantDLPPolicies = (state: RootState) =>
  state.tenantPolicies.policies.filter(p => p.policyType === 'DlpCompliancePolicy' && p.baseRules.length > 0);

// Selector to get AutoLabel policies only
export const selectTenantAutoLabelPolicies = (state: RootState) =>
  state.tenantPolicies.policies.filter(p => p.policyType === 'AutoSensitivityLabelPolicy' && p.baseRules.length > 0);

// Selector to get policy by ID
export const selectTenantPolicyById = (policyId: string) => (state: RootState) =>
  state.tenantPolicies.policies.find(p => p.policyId === policyId);

// ========== Export Reducer ==========

export default tenantPoliciesSlice.reducer;
